import type { PortalCycleSummary } from '@shared/portal-api-types';

export const PORTAL_CYCLE_QUERY_KEY = 'cycleId';

export function parsePortalPositiveId(
  search: string,
  key: string,
): number | null {
  const raw = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search).get(key);
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function parsePortalCycleId(search: string): number | null {
  return parsePortalPositiveId(search, PORTAL_CYCLE_QUERY_KEY);
}

export function getPortalCycleYearKey(cycle: PortalCycleSummary): string {
  const date = cycle.startDate ?? cycle.endDate;
  const year = date?.match(/^(\d{4})/)?.[1];
  return year ?? 'undated';
}

function sortPortalCycleYearKeys(left: string, right: string): number {
  if (left === 'undated') return 1;
  if (right === 'undated') return -1;
  return Number(right) - Number(left);
}

/** Default cycle when URL has no selection — newest year, first cycle in that year. */
export function getDefaultPortalCycleId(cycles: PortalCycleSummary[]): number | null {
  if (cycles.length === 0) return null;

  const yearKeys = Array.from(new Set(cycles.map(getPortalCycleYearKey))).sort(sortPortalCycleYearKeys);
  const firstYear = yearKeys[0];
  if (!firstYear) return cycles[0]?.id ?? null;

  const firstInYear = cycles.find((cycle) => getPortalCycleYearKey(cycle) === firstYear);
  return firstInYear?.id ?? cycles[0]?.id ?? null;
}

export function isPortalCycleAvailable(
  cycleId: number | null,
  cycles: PortalCycleSummary[],
): boolean {
  if (cycleId == null) return false;
  return cycles.some((cycle) => cycle.id === cycleId);
}

export interface PortalCycleSelectionResolution {
  cycleId: number | null;
  /** Replace the current URL entry (invalid/missing cycle correction). */
  replaceUrl: boolean;
}

export function resolvePortalCycleSelection(
  urlCycleId: number | null,
  cycles: PortalCycleSummary[],
): PortalCycleSelectionResolution {
  if (cycles.length === 0) {
    return { cycleId: urlCycleId, replaceUrl: false };
  }

  if (urlCycleId != null && isPortalCycleAvailable(urlCycleId, cycles)) {
    return { cycleId: urlCycleId, replaceUrl: false };
  }

  return {
    cycleId: getDefaultPortalCycleId(cycles),
    replaceUrl: true,
  };
}

export type PortalQueryParamValue = string | number | null | undefined;

/** Merge query updates into a pathname + search pair without dropping unrelated params. */
export function buildPortalHref(
  pathname: string,
  search: string,
  updates: Record<string, PortalQueryParamValue> = {},
): string {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);

  for (const [key, value] of Object.entries(updates)) {
    if (value == null || value === '') {
      params.delete(key);
    } else {
      params.set(key, String(value));
    }
  }

  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
}

/** Preserve cycleId (and optional extra keys) when linking between portal sections. */
export function buildPortalSectionHref(
  pathname: string,
  search: string,
  preserveKeys: string[] = [PORTAL_CYCLE_QUERY_KEY],
): string {
  const current = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const params = new URLSearchParams();

  for (const key of preserveKeys) {
    const value = current.get(key);
    if (value) params.set(key, value);
  }

  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
}

export function portalNavPathname(href: string): string {
  return href.split('?')[0] ?? href;
}
